import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const TermsOfService = () => {
  const lastUpdated = "March 15, 2026";
  const companyName = "The Motley Maker LLC";
  const appName = "AMSCompass";
  const contactEmail = "info@artisanmarketandstudio.com";
  const websiteUrl = "https://www.madeartisanmarkets.com";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1E3A5F] text-white py-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">Terms of Service</h1>
          <p className="text-white/80 mt-2">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">1. Acceptance of Terms</h2>
            <p>
              Welcome to {appName}, a business management platform for artisan entrepreneurs operated by {companyName} ("Company," "we," "us," or "our"). By accessing or using our web application, website, and related services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms").
            </p>
            <p>
              If you do not agree to these Terms, you may not access or use the Service. We reserve the right to modify these Terms at any time. Your continued use of the Service following any changes constitutes acceptance of those changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">2. Description of Service</h2>
            <p>
              {appName} provides artisan entrepreneurs with tools for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Product catalog and inventory management</li>
              <li>Event discovery, matching, and application tracking</li>
              <li>Sales tracking and financial analytics</li>
              <li>AI-powered pricing recommendations</li>
              <li>Media library management with cloud storage integrations</li>
              <li>Business planning and strategic tools</li>
            </ul>
            <p className="mt-4">
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">3. Account Registration</h2>
            <p>To use certain features of the Service, you must create an account. You agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and update your information to keep it accurate and current</li>
              <li>Maintain the security and confidentiality of your login credentials</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>
            <p className="mt-4">
              You must be at least 18 years old to create an account and use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">4. Subscription and Payments</h2>
            
            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">4.1 Free Trial</h3>
            <p>
              New users may be eligible for a free trial period. At the end of the trial, you will need to subscribe to a paid plan to continue accessing premium features.
            </p>

            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">4.2 Subscription Plans</h3>
            <p>
              We offer various subscription plans with different features and pricing. Plan details and pricing are available on our website and may change from time to time.
            </p>

            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">4.3 Billing</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Subscription fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable except as required by law or as explicitly stated</li>
              <li>You authorize us to charge your payment method for all fees incurred</li>
              <li>Failure to pay may result in suspension or termination of your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">4.4 Cancellation</h3>
            <p>
              You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. You will retain access to paid features until then.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">5. User Content</h2>
            
            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">5.1 Your Content</h3>
            <p>
              You retain ownership of all content you upload, submit, or create through the Service ("User Content"), including product information, images, event data, and business records.
            </p>

            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">5.2 License to Us</h3>
            <p>
              By submitting User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, process, and display your content solely for the purpose of providing and improving the Service.
            </p>

            <h3 className="text-xl font-semibold text-[#1E3A5F] mt-6 mb-3">5.3 Content Responsibilities</h3>
            <p>You are solely responsible for your User Content. You represent and warrant that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You own or have the necessary rights to use and share your content</li>
              <li>Your content does not violate any third-party rights</li>
              <li>Your content does not violate any applicable laws or regulations</li>
              <li>Your content is accurate and not misleading</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">6. Third-Party Integrations</h2>
            <p>
              The Service may integrate with third-party services, including Google Drive, Dropbox, and payment processors. Your use of these integrations is subject to the terms and privacy policies of those third parties.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We are not responsible for the availability, accuracy, or content of third-party services</li>
              <li>You may disconnect third-party integrations at any time through your Settings</li>
              <li>Disconnecting an integration may limit certain features of the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">7. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to the Service or other accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Resell or redistribute the Service without authorization</li>
              <li>Use the Service to send spam or unsolicited communications</li>
              <li>Impersonate any person or entity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">8. Intellectual Property</h2>
            <p>
              The Service, including its design, features, content, and underlying technology, is owned by {companyName} and protected by intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of the Service without our written permission.
            </p>
            <p className="mt-4">
              {appName}, our logo, and other marks are trademarks of {companyName}. You may not use these marks without our prior written consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">9. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="mt-4">
              We do not warrant that the Service will be uninterrupted, secure, or error-free. We do not warrant the accuracy or reliability of any information obtained through the Service, including AI-generated recommendations.
            </p>
            <p className="mt-4">
              AI-powered features, including pricing recommendations and event matching, are provided for informational purposes only. You are responsible for making your own business decisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p className="mt-4">
              OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless {companyName}, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your use of the Service</li>
              <li>Your User Content</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">12. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Upon termination:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your right to use the Service will immediately cease</li>
              <li>We may delete your account and User Content</li>
              <li>Provisions that by their nature should survive termination will survive</li>
            </ul>
            <p className="mt-4">
              You may terminate your account at any time by contacting us or through your account settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">13. Governing Law and Disputes</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of [Your State], without regard to its conflict of law provisions.
            </p>
            <p className="mt-4">
              Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive relief in court for intellectual property violations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">14. General Provisions</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and {companyName} regarding the Service.</li>
              <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions will continue in effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right or provision does not constitute a waiver.</li>
              <li><strong>Assignment:</strong> You may not assign these Terms without our consent. We may assign our rights freely.</li>
              <li><strong>Notices:</strong> We may provide notices through the Service, email, or other means.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">15. Contact Us</h2>
            <p>
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p><strong>{companyName}</strong></p>
              <p>Email: <a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline">{contactEmail}</a></p>
              <p>Website: <a href={websiteUrl} className="text-blue-600 hover:underline">{websiteUrl}</a></p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#1E3A5F] text-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/80">
            &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6">
            <Link to="/terms" className="text-white/80 hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="text-white/80 hover:text-white">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
